import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { MunicipalityAlarm, BuildingAlarm, BuildingAlarmResponse, EgAlarm } from '../models/alarms';
import { filter, map, distinctUntilChanged, switchMap, shareReplay, mergeMap, first, toArray } from 'rxjs/operators';
import { AlarmsService } from './alarms.service';
import { NotificationUserService } from './notification-user.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { MapStoreMunicipality } from '../../shared/models/municipality/Municipality';
import { NotificationUser } from '../models/notification-user';

@Injectable({
  providedIn: 'root',
})
export class AlarmsStoreService {
  private municipalityAlarmStore: BehaviorSubject<MunicipalityAlarm> = new BehaviorSubject<MunicipalityAlarm>({} as MunicipalityAlarm);
  private municipalityAlarmEventsStore: BehaviorSubject<EgAlarm[]> = new BehaviorSubject<EgAlarm[]>([]);
  private buildingAlarmsStore: BehaviorSubject<BuildingAlarm[]> = new BehaviorSubject<BuildingAlarm[]>([]);
  private removedRecipentsStore: BehaviorSubject<NotificationUser[]> = new BehaviorSubject<NotificationUser[]>([]);

  public readonly municipalityAlarm$ = this.municipalityAlarmStore.pipe(distinctUntilChanged());

  public readonly removedRecipients$ = this.removedRecipentsStore.asObservable();

  get municipalityAlarm(): MunicipalityAlarm | null {
    return this.municipalityAlarmStore.getValue();
  }
  get municipalityAlarmEvents(): any {
    return this.municipalityAlarmEventsStore.getValue();
  }
  get buildingAlarms(): BuildingAlarm[] {
    return this.buildingAlarmsStore.getValue();
  }
  get removedRecipients() {
    return this.removedRecipentsStore.getValue();
  }

  constructor(
    private readonly alarmsService: AlarmsService,
    private readonly authService: AuthService,
    private readonly notificationUserService: NotificationUserService,
    private readonly overlayLoaderService: OverlayLoaderService,
    private readonly userMunicipalitiesService: UserMunicipalitiesService,
  ) {
    this.municipalityAlarm$ = this.alarmCount();
  }

  public deleteNotificationUsers(municipalityKey: string, buildingId: string): void {
    const users = this.removedRecipients.filter((user: NotificationUser) => user.buildingId === buildingId);
    from(users)
      .pipe(
        mergeMap((user: NotificationUser) => {
          const usersRemaining = this.removedRecipients.filter((recipient: NotificationUser) => recipient.guid !== user.guid);
          this.removedRecipentsStore.next(usersRemaining);
          return this.notificationUserService.deleteNotificationUserByUserId(municipalityKey, user.buildingId, user.guid);
        }),
        toArray(),
        first(),
      )
      .subscribe(() => (this.overlayLoaderService.isLoading = false));
  }

  public removeNotificationUser(user: NotificationUser): void {
    const recipents = this.removedRecipients;
    recipents.push(user);
    this.removedRecipentsStore.next(recipents);
    this.overlayLoaderService.isLoading = false;
  }

  public clearNotificationUsers(): void {
    this.removedRecipentsStore.next([]);
  }

  private municipality$: Observable<MapStoreMunicipality> = this.userMunicipalitiesService.currentMunicipality$.pipe(
    filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
    distinctUntilChanged(),
  );

  private alarmCount(): Observable<MunicipalityAlarm> {
    return this.municipality$.pipe(
      filter(() => !!this.authService.permissions.ENERGY_GUARD),
      switchMap((municipality: MapStoreMunicipality) => this.getAlarmCount$(municipality)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getAlarmCount$(municipality: MapStoreMunicipality): Observable<MunicipalityAlarm> {
    return this.alarmsService.getoDataLicensedMetersCount(municipality.key, true).pipe(
      switchMap((buildingAlarmResponse: BuildingAlarmResponse) =>
        of(buildingAlarmResponse.data.reduce((prevValue, currentValue) => prevValue + currentValue.alarmsCount, 0)),
      ),
      map((alarmCount: number): MunicipalityAlarm => new MunicipalityAlarm(municipality.key, alarmCount)),
    );
  }
}
